import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { projectsApi } from '../../services/projectsApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { ExpenseModal } from './ExpenseModal.jsx';

// Only allow http(s) receipt links - guards against javascript:/data: URLs.
function safeHttpUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

const CSV_COLUMNS = [
  { key: 'expenseDate', label: 'Date' },
  { key: 'employeeName', label: 'Employee' },
  { key: 'category', label: 'Category' },
  { key: 'amount', label: 'Amount' },
  { key: 'currency', label: 'Currency' },
  { key: 'billable', label: 'Billable', map: (r) => (r.billable ? 'yes' : 'no') },
  { key: 'status', label: 'Status' },
  { key: 'description', label: 'Description' },
];

const STATUS_VARIANT = { OPEN: 'warning', APPROVED: 'success', REJECTED: 'danger', REIMBURSED: 'info' };

export function ExpensesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const expenses = useQuery({
    queryKey: ['projects', 'expenses'],
    queryFn: () => projectsApi.listExpenses({ limit: 200 }).then((r) => r.items),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => projectsApi.setExpenseStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', 'expenses'] }),
  });
  const remove = useMutation({
    mutationFn: (id) => projectsApi.deleteExpense(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects', 'expenses'] }),
  });

  const rows = expenses.data || [];
  const total = rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
  const billable = rows.filter((r) => r.billable).reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
  const openCount = rows.filter((r) => r.status === 'OPEN').length;

  const columns = [
    { key: 'expenseDate', label: t('expense.col.date'), render: (r) => formatDate(r.expenseDate) },
    { key: 'employeeName', label: t('expense.col.employee'), render: (r) => r.employeeName || '-' },
    { key: 'category', label: t('expense.col.category'), render: (r) => t(`expense.category.${r.category}`) },
    { key: 'amount', label: t('expense.col.amount'), align: 'right', render: (r) => formatCurrency(r.amount, r.currency) },
    {
      key: 'billable',
      label: t('expense.col.billable'),
      render: (r) => (r.billable ? <Badge variant="info">{t('time.yes')}</Badge> : <span className="muted">{t('time.no')}</span>),
    },
    {
      key: 'receiptUrl',
      label: t('expense.col.receipt'),
      render: (r) => {
        const safe = safeHttpUrl(r.receiptUrl);
        return safe ? <a href={safe} target="_blank" rel="noopener noreferrer">{t('expense.receiptLink')}</a> : '-';
      },
    },
    {
      key: 'status',
      label: t('col.status'),
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`expense.status.${r.status}`)}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          {r.status === 'OPEN' && (
            <>
              <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: r.id, status: 'APPROVED' })}>
                {t('expense.action.approve')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: r.id, status: 'REJECTED' })}>
                {t('expense.action.reject')}
              </Button>
            </>
          )}
          {r.status === 'APPROVED' && (
            <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: r.id, status: 'REIMBURSED' })}>
              {t('expense.action.reimburse')}
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => remove.mutate(r.id)}>
            {t('expense.action.delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('expense.title')}</h1>
          <p className="muted">{t('expense.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={rows} columns={CSV_COLUMNS} filename="spesen" />
          <Button variant="primary" onClick={() => setModalOpen(true)}>{t('expense.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('expense.metric.total')} value={formatCurrency(total)} />
        <MetricCard label={t('expense.metric.billable')} value={formatCurrency(billable)} trendVariant="success" />
        <MetricCard label={t('expense.metric.open')} value={String(openCount)} />
        <MetricCard label={t('expense.metric.count')} value={String(rows.length)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={expenses.isLoading} emptyMessage={t('expense.empty')} />

      <ExpenseModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
